'use client';

import { HTMLAttributes, forwardRef, ReactNode, useEffect, useLayoutEffect, useRef, useState, useCallback, KeyboardEvent as ReactKeyboardEvent } from 'react';
import styles from './Menu.module.css';
import Icon from '../primitives/Icon';

interface MenuItem {
  label: ReactNode;
  icon?: ReactNode;
  shortcut?: string;
  disabled?: boolean;
  /** Highlights the row as the active/selected option. */
  selected?: boolean;
  /** Renders the row in the danger colour (delete, clear, remove). */
  destructive?: boolean;
  onClick?: () => void;
  submenu?: MenuItem[];
  divider?: boolean;
  /** Non-interactive section heading — groups the items below it under a label. */
  heading?: boolean;
  /** Node rendered at the trailing edge of the row (e.g. a Switch for a toggle row). */
  trailing?: ReactNode;
  /** Keep the menu open after this item is chosen (e.g. radio/toggle rows in a filter panel). */
  keepOpen?: boolean;
}

interface MenuProps extends HTMLAttributes<HTMLDivElement> {
  trigger: ReactNode;
  items: MenuItem[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Which edge the popover aligns to. `right` opens inward from a right-edge trigger. */
  align?: 'left' | 'right';
  /** Preferred vertical open direction. `up` opens above the trigger (for bottom-anchored
   *  menus, e.g. a composer). Defaults to `down`. Either way the menu auto-flips to the
   *  opposite side when the preferred side lacks room and the other side has more. */
  direction?: 'down' | 'up';
  /** Optional section title in a header row above the items. */
  title?: string;
  /** Optional node on the right of the header (e.g. a "Clear" button). */
  headerAction?: ReactNode;
  /** Optional rich header node above the items (e.g. a profile row: avatar + handle). */
  header?: ReactNode;
}

/** The nearest ancestor that clips its overflow — the real vertical bound for a popover,
 *  since the menu can be hidden by whatever is painted just outside that box (e.g. a
 *  thread's summary footer). Returns its viewport rect, or null if nothing clips. */
function clipAncestor(el: HTMLElement): DOMRect | null {
  let node = el.parentElement;
  while (node) {
    // Only the vertical axis matters for an up/down flip. The computed overflow-y is
    // already promoted to `auto` when overflow-x is non-visible, so this also catches
    // the common `overflow-x: hidden; overflow-y: visible` container.
    if (/(auto|scroll|hidden|clip)/.test(getComputedStyle(node).overflowY)) return node.getBoundingClientRect();
    node = node.parentElement;
  }
  return null;
}

const MenuItemComponent = ({
  item,
  index,
  focusedIndex,
  onSelect,
  onFocus,
}: {
  item: MenuItem;
  index: number;
  focusedIndex: number;
  onSelect: (item: MenuItem) => void;
  onFocus: (index: number) => void;
}) => {
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusedIndex === index && itemRef.current) {
      itemRef.current.focus();
    }
  }, [focusedIndex, index]);

  if (item.divider) {
    return <div className={styles.divider} role="separator" />;
  }

  if (item.heading) {
    return <div className={styles.sectionHeading} role="presentation">{item.label}</div>;
  }

  const hasSubmenu = item.submenu && item.submenu.length > 0;

  return (
    <div
      ref={itemRef}
      className={`${styles.item} ${item.disabled ? styles.itemDisabled : ''} ${item.selected ? styles.itemSelected : ''} ${item.destructive ? styles.itemDestructive : ''} ${focusedIndex === index ? styles.itemFocused : ''}`}
      role="menuitem"
      tabIndex={-1}
      aria-disabled={item.disabled}
      onClick={() => {
        if (item.disabled) return;
        if (hasSubmenu) {
          setSubmenuOpen(!submenuOpen);
        } else {
          onSelect(item);
        }
      }}
      onMouseEnter={() => {
        onFocus(index);
        if (hasSubmenu) setSubmenuOpen(true);
      }}
      onMouseLeave={() => {
        if (hasSubmenu) setSubmenuOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' && hasSubmenu) {
          e.preventDefault();
          setSubmenuOpen(true);
        }
      }}
    >
      {item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
      <span className={styles.itemLabel}>{item.label}</span>
      {item.shortcut && <span className={styles.itemShortcut}>{item.shortcut}</span>}
      {item.trailing && <span className={styles.itemTrailing}>{item.trailing}</span>}
      {hasSubmenu && (
        <>
          <Icon name="chevron_right" size="md" className={styles.itemArrow} />
          {submenuOpen && (
            <div className={styles.submenu} role="menu">
              {item.submenu!.map((sub, i) => (
                <div
                  key={i}
                  className={`${styles.item} ${sub.disabled ? styles.itemDisabled : ''}`}
                  role="menuitem"
                  tabIndex={-1}
                  aria-disabled={sub.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!sub.disabled) onSelect(sub);
                  }}
                >
                  {sub.icon && <span className={styles.itemIcon}>{sub.icon}</span>}
                  <span className={styles.itemLabel}>{sub.label}</span>
                  {sub.shortcut && <span className={styles.itemShortcut}>{sub.shortcut}</span>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Menu = forwardRef<HTMLDivElement, MenuProps>(({
  trigger,
  items,
  open: controlledOpen,
  onOpenChange,
  align = 'left',
  direction = 'down',
  title,
  headerAction,
  header,
  className = '',
  ...props
}, ref) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  // Where the popover actually opens — starts at the preferred `direction` and flips
  // once measured if that side would overflow the viewport (see the layout effect below).
  const [resolvedDirection, setResolvedDirection] = useState<'down' | 'up'>(direction);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Keep the forwarded ref working while we also hold our own handle for measuring.
  const setMenuRef = useCallback((node: HTMLDivElement | null) => {
    menuRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as { current: HTMLDivElement | null }).current = node;
  }, [ref]);

  // While closed (or when the preference changes) fall back to the preferred direction,
  // so each open re-measures from a clean slate.
  useEffect(() => {
    if (!isOpen) setResolvedDirection(direction);
  }, [isOpen, direction]);

  // On open, flip up/down to whichever side actually has room. Runs before paint so the
  // menu never flashes in the wrong place. The room isn't bounded by the viewport but by
  // the nearest scroll/clip ancestor (e.g. a message thread) — anything painted below it,
  // like a summary footer, would otherwise cover a menu that opened downward.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const wrapper = wrapperRef.current;
    const menu = menuRef.current;
    if (!wrapper || !menu) return;

    const clip = clipAncestor(wrapper);
    const topBound = clip ? Math.max(clip.top, 0) : 0;
    const bottomBound = clip ? Math.min(clip.bottom, window.innerHeight) : window.innerHeight;

    const rect = wrapper.getBoundingClientRect();
    const menuH = menu.offsetHeight;
    const GAP = 8;
    const spaceBelow = bottomBound - rect.bottom;
    const spaceAbove = rect.top - topBound;
    let dir: 'down' | 'up' = direction;
    if (dir === 'down' && spaceBelow < menuH + GAP && spaceAbove > spaceBelow) dir = 'up';
    else if (dir === 'up' && spaceAbove < menuH + GAP && spaceBelow > spaceAbove) dir = 'down';
    setResolvedDirection(dir);
  }, [isOpen, direction, items.length]);

  const setOpen = useCallback((val: boolean) => {
    if (isControlled) onOpenChange?.(val);
    else setInternalOpen(val);
    if (!val) setFocusedIndex(-1);
  }, [isControlled, onOpenChange]);

  const selectableItems = items.filter(i => !i.divider && !i.heading && !i.disabled);

  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextItems = items.map((item, i) => ({ item, i })).filter(x => !x.item.divider && !x.item.heading && !x.item.disabled);
      const currentIdx = nextItems.findIndex(x => x.i === focusedIndex);
      const next = nextItems[(currentIdx + 1) % nextItems.length];
      if (next) setFocusedIndex(next.i);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevItems = items.map((item, i) => ({ item, i })).filter(x => !x.item.divider && !x.item.heading && !x.item.disabled);
      const currentIdx = prevItems.findIndex(x => x.i === focusedIndex);
      const prev = prevItems[(currentIdx - 1 + prevItems.length) % prevItems.length];
      if (prev) setFocusedIndex(prev.i);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[focusedIndex];
      if (item && !item.disabled && !item.divider && !item.heading) {
        item.onClick?.();
        if (!item.keepOpen) setOpen(false);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setOpen]);

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${className}`}
      onKeyDown={handleKeyDown}
      {...props}
    >
      <div
        className={styles.trigger}
        onClick={() => setOpen(!isOpen)}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(!isOpen);
          }
        }}
      >
        {trigger}
      </div>
      {isOpen && (
        <div ref={setMenuRef} className={`${styles.menu} ${align === 'right' ? styles.alignRight : ''} ${resolvedDirection === 'up' ? styles.openUp : ''}`} role="menu">
          {header && <div className={styles.customHeader}>{header}</div>}
          {(title || headerAction) && (
            <div className={styles.header}>
              {title && <span className={styles.headerTitle}>{title}</span>}
              {headerAction && <span className={styles.headerAction}>{headerAction}</span>}
            </div>
          )}
          {items.map((item, i) => (
            <MenuItemComponent
              key={i}
              item={item}
              index={i}
              focusedIndex={focusedIndex}
              onSelect={(selected) => {
                selected.onClick?.();
                if (!selected.keepOpen) setOpen(false);
              }}
              onFocus={setFocusedIndex}
            />
          ))}
        </div>
      )}
    </div>
  );
});

Menu.displayName = 'Menu';
export default Menu;
